
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { db } from "../firebase";

import {
  collection,
  addDoc,
  serverTimestamp
} from "firebase/firestore";

import "./ConfirmDeposit.css";

const TEN_MINUTES = 10 * 60;

export default function ConfirmDeposit() {
  const navigate = useNavigate();
  const { state } = useLocation();

  const {
    provider,
    number,
    fullname,
    amount
  } = state || {};

  const [username, setUsername] = useState("");
  const [phone, setPhone] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [loading, setLoading] = useState(false);

  const [timeLeft, setTimeLeft] = useState(TEN_MINUTES);
  const [showProcessing, setShowProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const depositRef = collection(db, "tsh_transactions");

  // Load username and start the 10-minute countdown.
  useEffect(() => {
    const savedUsername = localStorage.getItem("username");

    if (savedUsername) {
      setUsername(savedUsername);
    }

    const endTime = Date.now() + TEN_MINUTES * 1000;

    const updateCountdown = () => {
      const remaining = Math.max(
        0,
        Math.ceil((endTime - Date.now()) / 1000)
      );

      setTimeLeft(remaining);
    };

    updateCountdown();

    const timer = setInterval(updateCountdown, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    return `${String(minutes).padStart(2, "0")}:${String(
      remainingSeconds
    ).padStart(2, "0")}`;
  };

  const handleConfirm = async () => {
    setErrorMessage("");

    if (!username || !provider || !number || !fullname || !amount) {
      setErrorMessage(
        "Deposit details are missing. Please return and try again."
      );
      return;
    }

    if (!phone.trim() || !transactionId.trim()) {
      setErrorMessage(
        "Please enter your sender phone number and transaction ID."
      );
      return;
    }

    try {
      setLoading(true);

      await addDoc(depositRef, {
        username,
        provider,
        businessNumber: number,
        recipient: fullname,
        amount: Number(amount),
        phone: phone.trim(),
        transactionId: transactionId.trim(),
        currency: "TZS",
        type: "deposit",
        status: "processing",
        createdAt: serverTimestamp()
      });

      // Show the processing confirmation only after Firestore accepts the request.
      setShowProcessing(true);
    } catch (err) {
      console.error("Deposit submission failed:", err);

      setErrorMessage(
        "Failed to submit your deposit. Please check your connection and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="deposit-confirm-page">
      {/* 10-minute countdown */}
      <div className="deposit-countdown">
        <span className="countdown-label">
          Deposit time remaining
        </span>

        <strong className={timeLeft === 0 ? "time-expired" : ""}>
          {formatTime(timeLeft)}
        </strong>

        <span className="countdown-note">
          Please complete your payment and submit the details.
        </span>
      </div>

      <div className="deposit-header">
        <button
          type="button"
          className="back-button"
          onClick={() => navigate(-1)}
          disabled={loading}
        >
          🔙
        </button>

        <h2>Deposit Confirmation</h2>
      </div>

      <div className="deposit-summary">
        <div className="success-icon">💳</div>

        <h3>Payment Summary</h3>

        <div className="summary-row">
          <span>Amount</span>
          <b>TZS {Number(amount || 0).toLocaleString()}</b>
        </div>

        <div className="summary-row">
          <span>Provider</span>
          <b>{provider || "-"}</b>
        </div>

        <div className="summary-row">
          <span>Recipient</span>
          <b>{fullname || "-"}</b>
        </div>

        <div className="summary-row">
          <span>Account Number</span>
          <b>{number || "-"}</b>
        </div>
      </div>

      <div className="deposit-form">
        <label>Username</label>

        <input
          type="text"
          value={username}
          readOnly
          className="readonly-input"
        />

        <label>Sender Phone Number/Your Number</label>

        <input
          type="tel"
          inputMode="tel"
          placeholder="07XXXXXXXX"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={loading}
        />

        <label>Transaction ID / Message</label>

        <input
          type="text"
          placeholder="Example: DH23NXXXX"
          value={transactionId}
          onChange={(e) => setTransactionId(e.target.value)}
          disabled={loading}
        />


        <div className="deposit-warning">
          Please make sure you have completed the payment before
          submitting. Your deposit will be reviewed before approval.
        </div>

        {errorMessage && (
          <div className="deposit-error" role="alert">
            {errorMessage}
          </div>
        )}

        <button
          type="button"
          className="submit-deposit-btn"
          onClick={handleConfirm}
          disabled={loading}
        >
          {loading
            ? "Submitting..."
            : "Confirm Deposit / I Have Paid"}
        </button>
      </div>

      {/* Processing confirmation */}
      {showProcessing && (
        <div
          className="deposit-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="processing-title"
        >
          <div className="deposit-processing-modal">
            <div className="processing-icon">⏳</div>

            <h2 id="processing-title">
              Deposit Submitted!
            </h2>

            <div className="processing-status">
              PROCESSING
            </div>

            <p>
              Your deposit request has been received and is waiting
              for review.
            </p>

            <p className="processing-note">
              The funds will be credited after your payment has
              been reviewed and approved.
            </p>

            <button
              type="button"
              className="processing-ok-btn"
              onClick={() => navigate("/dashboard")}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
